import {
  LayoutDashboard, BarChart3, FileText, GitCompare,
  Euro, Wallet, Calculator, Target, Building2,
  Users, CreditCard, CalendarDays,
  Package, Carrot, Truck, Upload,
  UtensilsCrossed, Wine, BookOpen,
  ClipboardCheck, Brain,
  User, MessageSquare,
  Settings
} from "lucide-react";

/**
 * Canonical registry of all navigable items in MargeVio.
 * `key` maps to ModuleVisibility keys (null = always visible).
 */
export const ITEMS = {
  "uebersicht":        { path: "/dashboard/uebersicht",  label: "Dashboard",          icon: LayoutDashboard, key: null },
  "statistik":         { path: "/statistik",             label: "Statistik",          icon: BarChart3,       key: "statistik" },
  "monatsberichte":    { path: "/monatsberichte",        label: "Berichte",           icon: FileText,        key: "statistik" },
  "standort-vergleich":{ path: "/standort-vergleich",    label: "Standort-Vergleich", icon: GitCompare,      key: "statistik" },
  "einnahmen":         { path: "/einnahmen",             label: "Einnahmen",          icon: Euro,            key: "einnahmen" },
  "unternehmerlohn":   { path: "/unternehmerlohn",       label: "Verdienst",          icon: Wallet,          key: "verdienst" },
  "preisvorschlag":    { path: "/preisvorschlag",        label: "Preise",             icon: Calculator,      key: "preise" },
  "strategie":         { path: "/strategie",             label: "Strategie",          icon: Target,          key: "strategie" },
  "fixkosten":         { path: "/fixkosten",             label: "Fixkosten",          icon: Building2,       key: "fixkosten" },
  "mitarbeiter":       { path: "/mitarbeiter",           label: "Personal",           icon: Users,           key: "personal" },
  "mitarbeiter-zugang":{ path: "/mitarbeiter-zugang",    label: "MA-Zugang",          icon: CreditCard,      key: "personal" },
  "schichtplan":       { path: "/schichtplan",           label: "Schichtplan",        icon: CalendarDays,    key: "schichtplan" },
  "warenwirtschaft":   { path: "/warenwirtschaft",       label: "Lager",              icon: Package,         key: "lager" },
  "zutaten":           { path: "/zutaten",               label: "Zutaten",            icon: Carrot,          key: "zutaten" },
  "lieferanten":       { path: "/lieferanten",           label: "Lieferanten",        icon: Truck,           key: "lieferanten" },
  "import":            { path: "/import",                label: "Import",             icon: Upload,          key: null },
  "speisen":           { path: "/speisen",               label: "Speisen",            icon: UtensilsCrossed, key: "speisen" },
  "getraenke":         { path: "/getraenke",             label: "Getränke",           icon: Wine,            key: "getraenke" },
  "menue":             { path: "/menue",                 label: "Speisekarte",        icon: BookOpen,        key: "speisen" },
  "haccp":             { path: "/haccp",                 label: "HACCP",              icon: ClipboardCheck,  key: null },
  "ki-assistent":      { path: "/ki-assistent",          label: "KI-Assistent",       icon: Brain,           key: null, highlight: true },
  "konto":             { path: "/konto",                 label: "Konto",              icon: User,            key: null },
  "feedback":          { path: "/konto?tab=feedback",    label: "Feedback",           icon: MessageSquare,   key: null },
  "einstellungen":     { path: "/einstellungen",         label: "Einstellungen",      icon: Settings,        key: null },
};

/**
 * Icon-strings resolve to lucide components (serialisable via localStorage).
 */
export const PANEL_ICON_MAP = {
  LayoutDashboard, Euro, Users, Package, BookOpen,
  ClipboardCheck, User, Settings,
};

/**
 * Panel colour tokens. Kept as plain strings for easy JSON storage.
 */
const COLORS = {
  sky:     { grad: "from-sky-500/25 to-sky-900/5",     icon: "text-sky-300",     ring: "ring-sky-400/40",     dot: "bg-sky-400" },
  emerald: { grad: "from-emerald-500/25 to-emerald-900/5", icon: "text-emerald-300", ring: "ring-emerald-400/40", dot: "bg-emerald-400" },
  violet:  { grad: "from-violet-500/25 to-violet-900/5",   icon: "text-violet-300",  ring: "ring-violet-400/40",  dot: "bg-violet-400" },
  amber:   { grad: "from-amber-500/25 to-amber-900/5",     icon: "text-amber-300",   ring: "ring-amber-400/40",   dot: "bg-amber-400" },
  rose:    { grad: "from-rose-500/25 to-rose-900/5",       icon: "text-rose-300",    ring: "ring-rose-400/40",    dot: "bg-rose-400" },
  teal:    { grad: "from-teal-500/25 to-teal-900/5",       icon: "text-teal-300",    ring: "ring-teal-400/40",    dot: "bg-teal-400" },
  indigo:  { grad: "from-indigo-500/25 to-indigo-900/5",   icon: "text-indigo-300",  ring: "ring-indigo-400/40",  dot: "bg-indigo-400" },
  slate:   { grad: "from-slate-500/25 to-slate-900/5",     icon: "text-slate-300",   ring: "ring-slate-400/40",   dot: "bg-slate-400" },
};

export const PANEL_COLORS = COLORS;

/**
 * Default panel configuration. User can override via Einstellungen → Panels.
 */
export const DEFAULT_PANELS = [
  {
    id: "uebersicht",
    label: "Übersicht",
    icon: "LayoutDashboard",
    color: "sky",
    description: "Dashboard, Statistiken & Berichte",
    items: ["uebersicht", "statistik", "monatsberichte", "standort-vergleich"],
  },
  {
    id: "finanzen",
    label: "Finanzen",
    icon: "Euro",
    color: "emerald",
    description: "Einnahmen, Verdienst, Preise & Strategie",
    items: ["einnahmen", "unternehmerlohn", "preisvorschlag", "strategie", "fixkosten"],
  },
  {
    id: "personal",
    label: "Personal",
    icon: "Users",
    color: "violet",
    description: "Mitarbeiter, Zugang & Schichtpläne",
    items: ["mitarbeiter", "mitarbeiter-zugang", "schichtplan"],
  },
  {
    id: "lager",
    label: "Lager & Ware",
    icon: "Package",
    color: "amber",
    description: "Warenwirtschaft, Zutaten, Lieferanten",
    items: ["warenwirtschaft", "zutaten", "lieferanten", "import"],
  },
  {
    id: "speisekarte",
    label: "Speisekarte",
    icon: "BookOpen",
    color: "rose",
    description: "Speisen, Getränke & digitales Menü",
    items: ["speisen", "getraenke", "menue"],
  },
  {
    id: "betrieb",
    label: "Betrieb",
    icon: "ClipboardCheck",
    color: "teal",
    description: "HACCP & KI-Assistent",
    items: ["haccp", "ki-assistent"],
  },
  {
    id: "konto",
    label: "Konto",
    icon: "User",
    color: "indigo",
    description: "Profil, Abonnement & Zahlungen",
    items: ["konto"],
  },
  {
    id: "einstellungen",
    label: "Einstellungen",
    icon: "Settings",
    color: "slate",
    description: "Unternehmen, Standorte & Panels",
    items: ["einstellungen"],
  },
];

/**
 * Returns the panel that contains the given URL pathname, or null.
 */
export const findPanelByPath = (panels, pathname) => {
  if (!pathname) return null;
  // Panels overview itself → no active panel
  if (pathname === "/dashboard" || pathname === "/dashboard/" || pathname === "/") return null;

  for (const panel of panels) {
    for (const itemId of panel.items) {
      const item = ITEMS[itemId];
      if (!item) continue;
      const base = item.path.split("?")[0];
      if (pathname === base || pathname.startsWith(base + "/")) return panel;
    }
  }
  return null;
};
